'use client';

import { reportPriceLabel } from '@/lib/report-price';
import { Component, ErrorInfo, ReactNode, useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { capturePosthogEvent } from '@/lib/posthog';
import { savePdfReportInterest } from '@/lib/quiz';
import type { ReportInsight } from '@/lib/report/answer-evidence';
import ReportPreviewDialog from '@/components/report/ReportPreviewDialog';

const OFFER_CONTENT_VERSION = 'report-opening-v1';

// Error boundary catches RENDER errors anywhere inside the upsell tree
// (event-handler errors are caught by try/catch in the handlers themselves).
// Fires `pdf_report_upsell_error` with the error message + a stable component
// name so we can see in PostHog which sub-tree failed. Renders a minimal
// fallback so the rest of the results page stays usable.
class UpsellErrorBoundary extends Component<
  { children: ReactNode; onError: (message: string, stack: string) => void },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError(error?.message ?? String(error), info?.componentStack ?? '');
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-sm text-slate-600">
          We hit a snag rendering the report offer. Refresh to try again, or email
          contact@juleslemee.com and we&apos;ll send the full report manually.
        </div>
      );
    }
    return this.props.children;
  }
}

interface PdfReportUpsellProps {
  ideologyName: string;
  quizType: string;
  resultLabel: string;
  quizResponseId?: string;
  insight?: ReportInsight | null;
  insightPending?: boolean;
}

function PdfReportUpsellInner({
  ideologyName,
  quizType,
  resultLabel,
  quizResponseId,
  insight,
  insightPending = false,
}: PdfReportUpsellProps) {
  const posthog = usePostHog();
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const purchaseButtonRef = useRef<HTMLButtonElement>(null);
  const readerButtonRef = useRef<HTMLButtonElement>(null);
  const checkoutInFlight = useRef(false);
  const [readerResultId, setReaderResultId] = useState<string | null>(null);
  const readerReady = useRef(false);
  const exposedResultIds = useRef(new Set<string>());

  // Real Stripe checkout is gated on (a) public env flag and (b) the parent
  // having a saved ID for the quiz response (needed in webhook metadata
  // so we can sign a report URL after payment). Without one, fall back to the
  // existing email-waitlist behavior.
  const stripeEnabled =
    process.env.NEXT_PUBLIC_STRIPE_ENABLED === '1' && !!quizResponseId;
  const showReader = stripeEnabled && readerResultId === quizResponseId;
  const previewHref = quizResponseId
    ? `/quiz/results/report?preview=${encodeURIComponent(quizResponseId)}`
    : '/quiz/results/report?sample=1';

  useEffect(() => {
    const restore = () => {
      const url = new URL(window.location.href);
      setReaderResultId(stripeEnabled && url.searchParams.get('reportPreview') === '1'
        && url.searchParams.get('id') === quizResponseId ? quizResponseId! : null);
    };
    restore();
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, [quizResponseId, stripeEnabled]);

  const setReaderOpen = (open: boolean) => {
    const url = new URL(window.location.href);
    if (open && quizResponseId) {
      url.searchParams.set('id', quizResponseId);
      url.searchParams.set('type', 'long');
      url.searchParams.set('reportPreview', '1');
    } else {
      url.searchParams.delete('reportPreview');
    }
    window.history.replaceState(window.history.state, '', url);
    setReaderResultId(open ? quizResponseId ?? null : null);
    const properties = {
      quiz_response_id: quizResponseId ?? null,
      offer_content_version: OFFER_CONTENT_VERSION,
      has_verified_insight: !!insight,
      surface: 'results_dialog', personalised: !!insight,
    };
    if (open) capturePosthogEvent(posthog, 'pdf_report_sample_opened', properties, { sendToServer: true });
    else capturePosthogEvent(posthog, 'pdf_report_reader_closed', properties, { sendToServer: true });
  };

  useEffect(() => {
    if (!showReader) { readerReady.current = false; return; }
    if (insightPending || readerReady.current) return;
    readerReady.current = true;
    capturePosthogEvent(posthog, 'pdf_report_reader_ready', {
      quiz_response_id: quizResponseId ?? null,
      offer_content_version: OFFER_CONTENT_VERSION,
      has_verified_insight: !!insight,
      surface: 'results_dialog',
    }, { sendToServer: true });
  }, [showReader, insightPending, insight, posthog, quizResponseId]);


  // Historical mount event. It includes below-the-fold cards and must not
  // be interpreted as viewport exposure; use pdf_report_offer_visible for that.
  useEffect(() => {
    capturePosthogEvent(
      posthog,
      'pdf_report_upsell_viewed',
      {
        quiz_type: quizType,
        result_label: resultLabel,
        ideology_name: ideologyName,
        mode: stripeEnabled ? 'checkout' : 'waitlist',
        has_quiz_response_id: !!quizResponseId,
        offer_content_version: OFFER_CONTENT_VERSION,
        quiz_response_id: quizResponseId ?? null,
      },
      { sendToServer: true },
    );
    // posthog instance can be late-bound; we want this to fire on mount with
    // whatever instance is available. Re-firing if posthog flips from null to
    // ready is unnecessary noise, PostHog buffers pre-init events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const button = purchaseButtonRef.current;
    if (showReader || !stripeEnabled || !quizResponseId || !button ||
        typeof IntersectionObserver === 'undefined' || exposedResultIds.current.has(quizResponseId)) return;

    let intersects = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const clearTimer = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    };
    const update = () => {
      clearTimer();
      if (!intersects || document.visibilityState !== 'visible' ||
          exposedResultIds.current.has(quizResponseId)) return;
      timer = setTimeout(() => {
        timer = undefined;
        if (!intersects || document.visibilityState !== 'visible') return;
        // IntersectionObserver can deliver the scroll-away callback after this
        // timer on a busy browser. Check the button's current viewport area
        // before recording an impression.
        const rect = button.getBoundingClientRect();
        const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
        const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        if (rect.width <= 0 || rect.height <= 0 ||
            visibleWidth * visibleHeight < rect.width * rect.height * 0.5) return;
        exposedResultIds.current.add(quizResponseId);
        capturePosthogEvent(posthog, 'pdf_report_offer_visible', {
          quiz_type: quizType,
          result_label: resultLabel,
          ideology_name: ideologyName,
          mode: 'checkout',
          quiz_response_id: quizResponseId,
          visibility_target: 'purchase_cta',
          offer_content_version: OFFER_CONTENT_VERSION,
          has_verified_insight: !!insight,
          minimum_visible_ms: 1000,
          minimum_visible_ratio: 0.5,
        }, { sendToServer: true });
      }, 1000);
    };
    // A bounded target works on short mobile viewports. This measures the
    // button's viewport intersection, not attention or whether text was read.
    const observer = new IntersectionObserver(entries => {
      const entry = entries[entries.length - 1];
      intersects = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      update();
    }, { threshold: [0, 0.5] });
    observer.observe(button);
    document.addEventListener('visibilitychange', update);
    return () => {
      clearTimer();
      observer.disconnect();
      document.removeEventListener('visibilitychange', update);
    };
  }, [posthog, quizType, resultLabel, ideologyName, stripeEnabled, quizResponseId, insight, showReader]);

  // Direct-to-checkout path: skip the modal email collection (Stripe collects
  // email on its hosted page). This is the primary CTA when paywall is live.
  const handleBuyClick = async (surface: 'results_offer' | 'results_dialog' = 'results_offer') => {
    if (checkoutInFlight.current) return;
    checkoutInFlight.current = true;
    // Fire click event FIRST, before anything that could throw. If something
    // in this handler crashes silently, we still know the click happened.
    try {
      capturePosthogEvent(
        posthog,
        'pdf_report_upsell_clicked',
        {
          quiz_type: quizType,
          result_label: resultLabel,
          ideology_name: ideologyName,
          mode: stripeEnabled ? 'checkout' : 'waitlist',
          quiz_response_id: quizResponseId ?? null,
          offer_content_version: OFFER_CONTENT_VERSION,
          has_verified_insight: !!insight,
          surface,
        },
        { sendToServer: true },
      );
    } catch (telemetryError) {
      console.error('Failed to capture upsell click event:', telemetryError);
    }

    try {
      if (!stripeEnabled) {
        setShowWaitlistModal(true);
        checkoutInFlight.current = false;
        return;
      }
      setIsLoading(true);
      setErrorMsg(null);
      const cancelUrl = new URL(window.location.href);
      cancelUrl.searchParams.set('id', quizResponseId!);
      cancelUrl.searchParams.set('type', 'long');
      if (surface === 'results_dialog') cancelUrl.searchParams.set('reportPreview', '1');
      const resp = await fetch('/api/checkout/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quizResponseId,
          ideologyName,
          // Send the user back to the exact page they were on if they cancel
          // checkout, instead of Stripe's default fallback to /quiz/results.
          cancelUrl: cancelUrl.toString(),
          // Forward the anonymous PostHog distinct_id so the webhook can
          // stitch the eventual `pdf_report_purchased` event back to the
          // person who clicked the upsell.
          distinctId: posthog?.get_distinct_id?.(),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.url) {
        throw new Error(data?.error || `Checkout failed (${resp.status}).`);
      }
      capturePosthogEvent(
        posthog,
        'pdf_report_checkout_started',
        {
          quiz_type: quizType,
          result_label: resultLabel,
          ideology_name: ideologyName,
          quiz_response_id: quizResponseId ?? null,
        },
        { sendToServer: true },
      );
      window.location.href = data.url;
    } catch (error) {
      console.error('Failed to start checkout:', error);
      // Surface the failure to PostHog so silent breakage stops being silent.
      capturePosthogEvent(
        posthog,
        'pdf_report_upsell_click_error',
        {
          quiz_type: quizType,
          result_label: resultLabel,
          ideology_name: ideologyName,
          mode: stripeEnabled ? 'checkout' : 'waitlist',
          error_message: error instanceof Error ? error.message : String(error),
        },
        { sendToServer: true },
      );
      setErrorMsg("Couldn't reach checkout right now. Please try again in a moment.");
      setIsLoading(false);
      checkoutInFlight.current = false;
    }
  };

  const handleSubmitWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg(null);
    try {
      await savePdfReportInterest({ email, ideologyName, quizType, resultLabel });
      // Stitch the anonymous person to their email so prior quiz events
      // carry forward and we can measure interest → purchase across
      // sessions and devices.
      try {
        posthog?.identify(email, { email, interest_source: 'pdf_report_upsell' });
      } catch {
        // identify failures are non-fatal
      }
      capturePosthogEvent(
        posthog,
        'pdf_report_interest_submitted',
        {
          quiz_type: quizType,
          result_label: resultLabel,
          ideology_name: ideologyName,
          email,
          quiz_response_id: quizResponseId ?? null,
        },
        { sendToServer: true },
      );
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to save interest:', error);
      setErrorMsg("Couldn't save right now. Please try again in a moment.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseWaitlist = () => {
    setShowWaitlistModal(false);
    setTimeout(() => {
      setEmail('');
      setSubmitted(false);
      setErrorMsg(null);
    }, 300);
  };

  return (
    <>
      <div className="border-y border-[#16223d]/15 py-6 mb-7" data-testid="report-offer">
        <p className="text-xs font-semibold tracking-wide text-[#52658a] mb-2">FROM YOUR REPORT</p>
        <div className="ph-no-capture ph-mask" data-testid={insight ? 'report-offer-insight' : undefined}>
          <h3 className="font-display text-[23px] leading-tight mb-3">{insight?.title ?? 'There is more to your result than a label'}</h3>
          <p className="text-[15px] text-[#52658a] leading-relaxed mb-4">{insight?.summary ?? 'Your report looks at your answers alongside the ideas and debates behind your political tradition.'}</p>
        </div>
        {stripeEnabled && <button
          ref={readerButtonRef}
          type="button"
          onClick={() => setReaderOpen(true)}
          className="w-full rounded-xl bg-[#2a4173] hover:bg-[#1f3258] px-4 py-3.5 text-[15px] font-semibold text-white mb-3"
        >{insight || insightPending ? 'Read my report excerpt' : 'Preview the report'}</button>}
        <button
          ref={purchaseButtonRef}
          onClick={() => void handleBuyClick()}
          disabled={isLoading}
          className="w-full text-[#2a4173] font-semibold py-2.5 px-3 rounded-lg hover:bg-[#f5f7fa] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed text-sm leading-relaxed"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {stripeEnabled ? 'Opening secure checkout…' : 'Saving…'}
            </>
          ) : (
            <>
              {stripeEnabled ? `Get your full profile, ${reportPriceLabel()}` : 'Notify me when it is ready'}
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </>
          )}
        </button>

        <p className="mt-2 text-center text-xs leading-relaxed text-[#52658a]">You pay once for the full report, which you can read online or save and print.</p>

        {errorMsg && (
          <div
            role="alert"
            className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {errorMsg}
          </div>
        )}

        {stripeEnabled ? null : (
          <p className="text-[11px] text-[#16223d]/55 mt-3 leading-relaxed">
            We are finishing this report and will email you the moment it is ready.
          </p>
        )}
      </div>
      <ReportPreviewDialog
        open={showReader}
        onClose={() => setReaderOpen(false)}
        insight={insight ?? null}
        pending={insightPending}
        ideologyName={ideologyName}
        previewHref={previewHref}
        onBuy={() => void handleBuyClick('results_dialog')}
        buying={isLoading}
        error={errorMsg}
        returnFocusRef={readerButtonRef}
      />

      {/* Waitlist fallback modal (only used when stripeEnabled is false) */}
      {showWaitlistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={handleCloseWaitlist}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {!submitted ? (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">We&apos;re still polishing this</h3>
                  <p className="text-slate-600 text-sm">
                    The Personal Political Profile is in final review. Leave your email and we&apos;ll
                    send it the moment it&apos;s ready.
                  </p>
                </div>

                <form onSubmit={handleSubmitWaitlist} className="space-y-4">
                  <div>
                    <label htmlFor="waitlist-email" className="block text-sm font-medium text-slate-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="waitlist-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500 outline-hidden transition-all"
                    />
                  </div>

                  {errorMsg && (
                    <div
                      role="alert"
                      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    >
                      {errorMsg}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
                    className="w-full bg-[#2a4173] hover:bg-[#1f3258] text-white font-semibold py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Saving…' : "Notify me"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <h3 className="text-xl font-bold text-slate-900 mb-2">You&apos;re on the list.</h3>
                <p className="text-slate-600 text-sm mb-6">
                  We&apos;ll send the report the moment it&apos;s ready.
                </p>
                <button
                  onClick={handleCloseWaitlist}
                  className="bg-gray-100 text-slate-700 font-medium py-2.5 px-6 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Default export wraps the inner component in an error boundary that captures
// render errors to PostHog. Click-handler errors are caught inside the handler
// itself; this catches anything that throws during the render phase.
export default function PdfReportUpsell(props: PdfReportUpsellProps) {
  return (
    <UpsellErrorBoundary
      onError={(message, stack) => {
        // Use the global posthog from window since the class component is
        // outside the React function context. Safe because PostHog Provider
        // wraps the whole app and exposes window.posthog.
        const w = typeof window !== 'undefined' ? (window as Window & { posthog?: { capture: (event: string, props: Record<string, unknown>) => void } }) : undefined;
        try {
          w?.posthog?.capture('pdf_report_upsell_error', {
            quiz_type: props.quizType,
            result_label: props.resultLabel,
            ideology_name: props.ideologyName,
            error_message: message,
            component_stack: stack.slice(0, 500),
          });
        } catch {
          /* swallow telemetry failures */
        }
        console.error('[PdfReportUpsell] render error:', message);
      }}
    >
      <PdfReportUpsellInner {...props} />
    </UpsellErrorBoundary>
  );
}
